import { DatePipe } from '@angular/common';
import {
    AfterViewInit,
    Component,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import clone from 'just-clone';
import { combineLatest, Observable, ReplaySubject, Subscription } from 'rxjs';
import {
    debounceTime,
    filter,
    map,
    startWith,
    takeWhile
} from 'rxjs/operators';
import * as fromLookups from '../../../lookups';
import { ITask } from '../../../lookups/ITask';
import * as fromShared from '../../../shared';
import { IShowErrors } from '../../../shared/errors-sidenav';
import { LoggingService } from '../../../shared/services/logging/logging.service';
import { IUser } from '../../../user-info/IUser';
import * as fromUser from '../../../user-info/store/user.reducer';
import { FixedPriceComponent } from '../../components/fixed-price-tab/fixed-price.component';
import { IFixedPrice } from '../../IFixedPrice';
import * as fromFixedPriceA from '../../store/actions';
import { DeleteFixedPrice, UpdateFixedPrice } from '../../store/actions';
import * as fromFixedPriceR from '../../store/reducer';
@Component({
    selector: 'ef-fixed-price-shell',
    templateUrl: './fixed-price-shell.component.html',
    styleUrls: ['./fixed-price-shell.component.scss'],
    standalone: false
})
export class FixedPriceShellComponent
    implements OnInit, OnDestroy, AfterViewInit
{
    errors$: Observable<string[]>;
    selectedError$: Observable<IShowErrors>;

    private userSub: Subscription;
    private active = true;
    public fyStartDate: Date;
    public fyEndDate: Date;
    public startDate: string;
    public endDate: string;
    public fpinvoices$: Observable<Array<IFixedPrice>>;
    public fpinvoices: Array<IFixedPrice> = [];
    public tasks$: Observable<Array<ITask>>;
    public tasks: Array<ITask>;
    public filteredByLaborTypeTasks: Array<ITask> = [];
    public filteredByCNTasks: Array<ITask> = [];
    public filteredInvoices: Array<IFixedPrice> = [];
    process$: Observable<fromShared.Process>;
    ShowError = new ReplaySubject<fromShared.Errors.IShowErrors>(1);
    @Input() public user: IUser;
    @ViewChild(FixedPriceComponent, { static: true })
    fixedPriceComp: FixedPriceComponent;
    startDateFilter: Date;
    endDateFilter: Date;
    constructor(
        private lookupsStore: Store<fromLookups.LookupState>,
        private fixedPriceStore: Store<fromFixedPriceR.State>,
        @Inject(LoggingService) private log: LoggingService,
        private store: Store<fromUser.State>,
        private datePipe: DatePipe,
        private errorStore: Store<fromShared.Errors.State>
    ) {}

    transformDate(date): Date {
        return new Date(this.datePipe.transform(date, 'yyyy-MM-dd'));
    }

    ngOnInit() {
        this.store.dispatch(new fromLookups.LoadTasks());

        this.process$ = this.store.pipe(select(fromShared.selectCurrent));
        // getting the fy start and end date from the user info data in the store
        this.userSub = this.store
            .pipe(
                takeWhile(_ => this.active),
                select(fromUser.getUser)
            )
            .subscribe(user => {
                this.log.debug({
                    type: 'FixedPriceShellComponent:user-subscribe',
                    user
                });
                this.user = user;

                if (user.userInfo) {
                    this.startDate =
                        user.userInfo.LastInvoiceStart &&
                        user.userInfo.LastInvoiceStart.toString();
                    this.endDate =
                        user.userInfo.FYEndDate &&
                        user.userInfo.FYEndDate.toString();
                    this.fyStartDate = new Date(this.startDate);
                    this.fyEndDate = new Date(this.endDate);
                    this.fyStartDate = this.transformDate(this.fyStartDate);
                    this.fyEndDate = this.transformDate(this.fyEndDate);
                }
            });

        // init observables
        // don't reassign after this
        this.fpinvoices$ = this.fixedPriceStore.pipe(
            select(fromFixedPriceR.getFixedPrice),
            filter(x => !!x && x.length > 0),
            map(items => clone(items)),
            map(x => this.zeroToNull(x))
        );
        // don't reassign after this
        this.tasks$ = this.lookupsStore.pipe(
            select(fromLookups.getTasksLookup)
        );

        this.errors$ = this.store.pipe(
            select(fromFixedPriceR.getFixedPriceErrors)
        );

        this.selectedError$ = this.errorStore.pipe(
            select(fromShared.Errors.getShowErrors)
        );
    }
    zeroToNull(items): IFixedPrice[] {
        items.forEach(x => {
            if (x.ScheduledAmount == 0) {
                x.ScheduledAmount = null;
            }
            if (x.InvoiceAmount == 0) {
                x.InvoiceAmount = null;
            }
        });
        return items;
    }
    ngAfterViewInit() {
        combineLatest([this.process$, this.ShowError])
            .pipe(takeWhile(() => this.active))
            .subscribe(([process, event]) => {
                this.store.dispatch(
                    new fromShared.Errors.SetShowErrors({
                        open: event.open,
                        MsgEntityID: event.MsgEntityID,
                        MsgProcessID: process.ProcessID
                    })
                );
            });

        // // subscribing to fpinvoices in the redux store
        // this.fpinvoices$
        //     .pipe(takeWhile(_ => this.active))
        //     .subscribe(invoices => {
        //         this.fpinvoices = invoices;
        //     });

        const dates$ = combineLatest([
            this.fixedPriceComp.selectStartDate.pipe(
                startWith(this.startDate)
            ),
            this.fixedPriceComp.selectEndDate.pipe(startWith(this.fyEndDate))
        ]);

        // subscribing to the dates in the store and making a call to the service when new dates are picked
        dates$
            .pipe(
                takeWhile(_ => this.active),
                debounceTime(250)
            )
            .subscribe(x => {
                if (x) {
                    this.startDateFilter = new Date(x[0]);
                    this.endDateFilter = new Date(x[1]);
                }
                this.fixedPriceStore.dispatch(
                    new fromFixedPriceA.LoadFixedPrice({
                        Txnstartdate: new Date(x[0]),
                        Txnenddate: new Date(x[1]),
                        Tasks: [],
                        CalcType: 'FP',
                        CNOnly: false
                    })
                );
            });

        // filtering the tasks based on labor type and task level
        combineLatest([
            this.tasks$,
            this.fpinvoices$,
            this.fixedPriceComp.toggleTaskLevel.pipe(startWith(false))
        ])
            .pipe(takeWhile(_ => this.active))
            .subscribe(x => {
                const tasks = x[0];
                const invoices = x[1];
                const taskLevel = x[2];
                this.filteredByLaborTypeTasks = tasks.filter(
                    (task: ITask) => task.ContractType === 'FFP'
                );
                this.filteredByCNTasks = this.filteredByLaborTypeTasks.filter(
                    (task: ITask) =>
                        task.IsChargeNumber === !taskLevel || taskLevel
                );
                if (this.filteredByCNTasks.length) {
                    this.fpinvoices = invoices.filter(i =>
                        this.filteredByCNTasks.some(
                            t => t.TaskLongName === i.TaskLongName
                        )
                    );
                } else {
                    this.fpinvoices = invoices;
                }
                this.log.debug({
                    type: 'FixedPriceShellComponent:toggleTaskLevel',
                    filteredTasks: this.filteredByCNTasks,
                    taskLevel
                });
            });
    }

    ngOnDestroy() {
        // this is for any observable subscriptions so we don't leak memory
        this.active = false;
    }

    // event that triggers a call to the service for updates
    public onApplyMultiClicked($event) {
        // const txnDate = moment($event.TxnDate);
        // if (txnDate.isValid() == true) {
        if ($event.type == 'Multi') {
            this.fixedPriceStore.dispatch(new UpdateFixedPrice($event.data));
        } else {
            this.fixedPriceStore.dispatch(new UpdateFixedPrice([$event.data]));
        }
        
        // }
    }

    OnShowError(event) {
        this.ShowError.next(event);
    }

    onDeleteFixedPrice($event) {
        const fpIDs = [];
        fpIDs.push($event.DftID);
        this.fixedPriceStore.dispatch(new DeleteFixedPrice(fpIDs));
        this.fixedPriceStore.dispatch(
            new fromFixedPriceA.LoadFixedPrice({
                Txnstartdate: this.startDateFilter,
                Txnenddate: this.endDateFilter,
                Tasks: [],
                CalcType: 'FP',
                CNOnly: false
            })
        );
    }
}
